import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DollarSign, FileText, Zap, TrendingUp } from "lucide-react";
import { useAdminAuth } from "@/hooks/use-admin-auth";
import { supabase } from "@/integrations/supabase/client";

const Dashboard = () => {
  const { isAdmin, loading, signOut } = useAdminAuth();
  const [stats, setStats] = useState({
    totalQuotes: 0,
    totalChallenges: 0,
    acceptedOffers: 0,
    revenue: 0
  });

  useEffect(() => {
    const fetchStats = async () => {
      const { data: quotes } = await supabase.from('quotes').select('*');
      const { data: challenges } = await supabase.from('challenges').select('*');
      
      const totalQuotes = quotes?.length || 0;
      const totalChallenges = challenges?.length || 0;
      const acceptedOffers = challenges?.filter(c => c.status === 'accepted').length || 0;
      const revenue = quotes?.reduce((sum, q) => sum + Number(q.estimated_price || 0), 0) || 0;

      setStats({ totalQuotes, totalChallenges, acceptedOffers, revenue });
    };

    if (isAdmin) {
      fetchStats();
    }
  }, [isAdmin]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
          <p className="text-muted-foreground mb-4">You don't have admin privileges.</p>
          <Button asChild>
            <Link to="/admin/login">Return to Login</Link>
          </Button>
        </div>
      </div>
    );
  }

  const statsDisplay = [
    { label: "New Quotes", value: stats.totalQuotes.toString(), icon: FileText, link: "/admin/quotes" },
    { label: "Challenge Submissions", value: stats.totalChallenges.toString(), icon: Zap, link: "/admin/offers" },
    { label: "Offers Accepted", value: stats.acceptedOffers.toString(), icon: TrendingUp, link: "/admin/offers" },
    { label: "Total Revenue", value: `$${stats.revenue.toLocaleString()}`, icon: DollarSign, link: "/admin/offers" }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-8">
              <img src="/assets/logo.png" alt="OneWeekMVPs" className="h-8" />
              <nav className="flex gap-6">
                <Link to="/admin/dashboard" className="text-sm font-medium text-foreground">
                  Dashboard
                </Link>
                <Link to="/admin/quotes" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Quotes
                </Link>
                <Link to="/admin/offers" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Offers
                </Link>
              </nav>
            </div>
            <Button variant="outline" onClick={signOut}>
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="font-heading text-4xl mb-2">Admin Dashboard</h1>
          <p className="text-muted-foreground">Welcome back! Here's your overview.</p>
        </div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statsDisplay.map((stat) => (
            <Link key={stat.label} to={stat.link}>
              <Card className="p-6 border-accent/20 hover:border-accent/40 transition-colors cursor-pointer">
                <div className="flex items-start justify-between mb-4">
                  <div className="p-2 bg-accent/10 rounded-lg">
                    <stat.icon className="w-5 h-5 text-accent" />
                  </div>
                </div>
                <div className="text-3xl font-heading mb-1">{stat.value}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </Card>
            </Link>
          ))}
        </div>

        {/* Quick Links */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="p-6 border-accent/20">
            <h2 className="font-heading text-2xl mb-4">Recent Quotes</h2>
            <p className="text-muted-foreground mb-4">View and manage calculator submissions</p>
            <Button asChild>
              <Link to="/admin/quotes">View All Quotes</Link>
            </Button>
          </Card>

          <Card className="p-6 border-accent/20">
            <h2 className="font-heading text-2xl mb-4">Manage Offers</h2>
            <p className="text-muted-foreground mb-4">Create and track flashbuild offers</p>
            <Button asChild>
              <Link to="/admin/offers">Manage Offers</Link>
            </Button>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
